import { SourceLinks } from '@/components/ui/source-links';
import { Sparkles, Scroll } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { DiscoveryCard } from './discovery-card';
import { cn } from '@/lib/utils';

interface AneParallelCardProps {
    parallel: {
        parallel: string;
        description: string;
        significance: string;
        sources: Source[];
    };
    isExpanded: boolean;
    onToggleExpansion: () => void;
    thumbnailUrl?: string;
}

export function AneParallelCard({
    parallel,
    isExpanded,
    onToggleExpansion,
}: AneParallelCardProps) {
    const isDarkMode = useDarkMode();

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            icon={Scroll}
            header={
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 relative z-10">
                    <div className="flex flex-col gap-3 flex-1 min-w-0">
                        <h4 className="text-xl font-serif font-medium tracking-tight text-foreground/90 leading-tight">
                            {parallel.parallel}
                        </h4>

                        {parallel.description && !isExpanded && (
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4 italic font-light">
                                    {parallel.description}
                                </p>
                                <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-primary/40 group-hover/card:text-primary/70 transition-colors">
                                    <Sparkles className="h-2.5 w-2.5" />
                                    <span>Discover Parallel</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            }
        >
            <div className="space-y-6 mt-4 relative z-10">
                {/* Description */}
                <div className="space-y-1.5 px-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Description</div>
                    <p className="text-sm text-foreground/70 font-light leading-relaxed">{parallel.description}</p>
                </div>

                {/* Significance */}
                <div className="space-y-2 px-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 flex items-center gap-2">
                        <Sparkles className="h-3 w-3" />
                        Significance
                    </div>
                    <p className="text-sm text-foreground/70 font-light leading-relaxed italic border-l-2 border-primary/20 pl-4 py-0.5">{parallel.significance}</p>
                </div>

                {/* Sources */}
                {parallel.sources?.length > 0 && (
                    <div className="pt-4 border-t border-border/10">
                        <SourceLinks sources={parallel.sources} />
                    </div>
                )}
            </div>
        </DiscoveryCard>
    );
}
